import { intersection } from "lodash";
import moment from "moment";

import type { PollService } from "@busbud/core-types";
import type {
  PublicSegment,
  PublicSegmentV4,
  RelatedPublicSegment,
  RelatedPublicSegmentV4
} from "@busbud/core-types/dist/lib/poll-service/api";
import { VehicleType } from "@busbud/int-schemas/lib/vehicles";
import type { TripLeg } from "@busbud/int-schemas/udm";
import { TicketType } from "@busbud/int-schemas/udm";

import type { NapiV2CartSegment } from "@app/types/napi-v2-cart-types";
import type { ResultsReduxState } from "@app/types/results-redux-types";
import type { TripOptionsMatrix } from "@app/types/results-types";
import type { SearchInfo } from "@app/types/search-types";

import { getSearchedLeg } from "./get-searched-leg";
import { getDurationInMinutes } from "../../helpers/time";
import { isBusFamilyVehicle } from "../../modules/results/utils";
import { isRoundtrip } from "../search";

export const isEticket = (
  ticket_type: PollService.API.RelatedPublicSegment["ticket_types"][number]
) => {
  return [TicketType.ETicket, TicketType.SupplierEmail].includes(ticket_type);
};

export const isBoBTrip = (
  trip:
    | PollService.API.RelatedPublicTrip
    | PollService.API.PublicTrip
    | PollService.API.RelatedPublicTripV4
    | PollService.API.PublicTripV4
) => {
  return trip.metadata.sellable;
};

export const isBoXTrip = (
  trip: PollService.API.RelatedPublicTrip | PollService.API.RelatedPublicTripV4
) => {
  return !isBoBTrip(trip);
};

export const isBoXOnewayTrip = (
  search: SearchInfo,
  trip: PollService.API.RelatedPublicTrip | PollService.API.RelatedPublicTripV4
) => {
  return isBoXTrip(trip) && !isRoundtrip(search);
};

export const getSegmentsFromTripLeg = (
  trip_leg: PollService.API.PublicTripLeg,
  segments: ResultsReduxState["trips_segments"]
): (
  | PollService.API.RelatedPublicSegment
  | PollService.API.RelatedPublicSegmentV4
)[] => {
  // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return trip_leg.segment_ids.map(id => segments[id]);
};

export const getLegSegmentsFromTrip = (
  search: SearchInfo,
  trip: PollService.API.RelatedPublicTrip | PollService.API.RelatedPublicTripV4,
  segments: ResultsReduxState["trips_segments"]
): (
  | PollService.API.RelatedPublicSegment
  | PollService.API.RelatedPublicSegmentV4
)[] => {
  return getSegmentsFromTripLeg(getSearchedLeg(search, trip), segments);
};

export const getVehicleCategory = (
  trip_leg: PollService.API.PublicTripLeg,
  segments: ResultsReduxState["trips_segments"]
): VehicleType => {
  // @ts-expect-error TS2538 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const first_segment = segments[trip_leg.segment_ids[0]];
  // First segment vehicle type determines the category of the whole trip
  const vehicle_type = first_segment.vehicle.type;

  return !vehicle_type || isBusFamilyVehicle(vehicle_type)
    ? VehicleType.Bus
    : vehicle_type;
};

export interface TripDurationSegments {
  departure_time: {
    timestamp: string;
  };
  arrival_time?: {
    timestamp: string | undefined;
  } | null;
}

export const getTripDuration = (trip_segments: TripDurationSegments[]) => {
  const first_segment = trip_segments[0];
  const last_segment = trip_segments[trip_segments.length - 1];
  // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const unreliable_duration = !last_segment.arrival_time?.timestamp;

  if (unreliable_duration) {
    return 0;
  }

  return getDurationInMinutes(
    // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    first_segment.departure_time.timestamp,
    // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    last_segment.arrival_time?.timestamp
  );
};

export const isInterconnection = (
  trip:
    | PollService.API.RelatedPublicTrip
    | PollService.API.PublicTrip
    | PollService.API.RelatedPublicTripV4
    | PollService.API.PublicTripV4
    | undefined,
  trip_leg: PollService.API.PublicTripLeg | undefined
) => {
  if (!trip || !trip_leg) {
    return false;
  }

  if (trip_leg.segment_ids.length === 1) {
    return false;
  }

  const trip_prices = trip.prices.filter(
    // @ts-expect-error can't type BooleanConstructor
    price => intersection(price.segment_ids, trip_leg.segment_ids).length > 0
  );

  const is_interconnection = trip_prices.length > 1;

  return is_interconnection;
};

export const isDirectLeg = (leg: PollService.API.PublicTripLeg) => {
  return leg.segment_ids.length === 1;
};

export const hasChangeOfStation = (
  trip_leg: PollService.API.PublicTripLeg,
  segments: ResultsReduxState["trips_segments"]
) => {
  const trip_segments = trip_leg.segment_ids;

  if (trip_segments.length === 1) {
    return false;
  }

  for (let i = 0; i < trip_segments.length - 1; i++) {
    // @ts-expect-error TS2538 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const trip_segment = segments[trip_segments[i]];
    // @ts-expect-error TS2538 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const next_trip_segment = segments[trip_segments[i + 1]];
    if (trip_segment.destination_id !== next_trip_segment.origin_id) {
      return true;
    }
  }

  return false;
};

type Stats = { count: number; total_duration: number };
export type StopStats = {
  stops: Stats;
  transfers: Stats;
};
export function getTripStopsStats(
  segments: Record<
    string,
    | RelatedPublicSegment
    | NapiV2CartSegment
    | PublicSegment
    | RelatedPublicSegmentV4
    | PublicSegmentV4
  >,
  trip_leg: TripLeg
): StopStats {
  const transfers_stats: Stats = {
    count: 0,
    total_duration: 0
  };
  const stops_stats: Stats = {
    count: 0,
    total_duration: 0
  };
  const trip_segments_ids = trip_leg.segment_ids || [];

  transfers_stats.count = trip_segments_ids?.length - 1;

  trip_segments_ids.forEach((segment_id, index) => {
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    const segment:
      | RelatedPublicSegment
      | NapiV2CartSegment
      | PublicSegment
      | RelatedPublicSegmentV4
      | PublicSegmentV4 = segments[segment_id];

    if (segment) {
      (segment.stops || []).forEach(stop => {
        const stop_duration =
          stop.departure_time && stop.arrival_time
            ? moment(stop.departure_time.timestamp).diff(
                stop.arrival_time.timestamp,
                "minutes"
              )
            : 0;

        stops_stats.count++;
        stops_stats.total_duration += stop_duration || 0;
      });

      if (index > 1) {
        const transfer_duration = segment.arrival_time
          ? moment(segment.departure_time.timestamp).diff(
              segment.arrival_time.timestamp,
              "minutes"
            )
          : 0;

        transfers_stats.total_duration += transfer_duration || 0;
      }
    }
  });

  return {
    stops: stops_stats,
    transfers: transfers_stats
  };
}

export function getDefaultTripId(
  trip_options_matrix: TripOptionsMatrix
): string | undefined {
  return (
    trip_options_matrix.flat().find(matrix => !matrix.is_sold_out)?.trip_id ||
    undefined
  );
}

export function getDefaultTripIdByJourneyId(
  trip_options_matrix: TripOptionsMatrix,
  journey_id: string
): string | undefined {
  return (
    trip_options_matrix.flat().find(matrix => {
      return matrix.journey_id === journey_id && !matrix.is_sold_out;
    })?.trip_id || undefined
  );
}
